import { z } from "zod/v4";

/**
 * Benchmarks for one store, plus the population statistics it is measured
 * against.
 *
 * Both halves are returned because they have different availability, and the
 * audit needs to work when only the second exists. A store's own row requires
 * our pixel to have been collecting; a merchant who installed this morning has
 * none.
 *
 * **Only `all` is a population statistic.** `peer` and `industry` are computed
 * per store and come off that store's own row, so they mean something only when
 * `store` is non-null. COUNTED on L90D: `margin.peer` holds 5,566 distinct
 * values across 5,809 rows, `margin.industry` holds 16, and `margin.all` holds
 * one. For a store with no row the backend picks a medians row arbitrarily
 * (`LIMIT 1`, no `ORDER BY`), so `peer` and `industry` then describe one
 * unrelated store.
 *
 * Read them through `AuditStoreBenchmarks.populationMedian`, which applies that
 * rule. A new store can still be compared against `all`, using a funnel derived
 * from its own Shopify data.
 */

export const BenchmarkPeriodList = ["L30D", "L60D", "L90D"] as const;
export const BenchmarkPeriodSchema = z.enum(BenchmarkPeriodList);
export type BenchmarkPeriod = z.infer<typeof BenchmarkPeriodSchema>;

/**
 * Everything the table carries, not just the funnel. Later audit decisions
 * need `freeShippingPct`, `margin`, `unitsPerTransaction` and `discountRate`,
 * and it is one row either way — fetching narrow now would mean changing the
 * contract again.
 */
export const BenchmarkMetricKeyList = [
  "collectionRate",
  "pdpRate",
  "atcRate",
  "cartToCheckoutRate",
  "checkoutToPurchaseRate",
  "conversionRate",
  "bounceRate",
  "cartAbandonmentRate",
  "checkoutAbandonmentRate",
  "revenuePerSession",
  "revenuePerVisitor",
  "profitPerSession",
  "profitPerVisitor",
  "margin",
  "aov",
  "unitsPerTransaction",
  "discountRate",
  "freeShippingPct",
  "returnRate",
] as const;

export const BenchmarkMetricKeySchema = z.enum(BenchmarkMetricKeyList);
export type BenchmarkMetricKey = z.infer<typeof BenchmarkMetricKeySchema>;

/**
 * A percentile says where the store sits; a median says what "normal" is.
 * Both are nullable throughout: a metric can be absent for a store (no COGS,
 * so no margin) independently of whether the population has it.
 */
export const BenchmarkMetricSchema = z.object({
  value: z.number().nullable(),
  percentileAll: z.number().nullable(),
  percentileIndustry: z.number().nullable(),
  percentilePeer: z.number().nullable(),
});

export type BenchmarkMetric = z.infer<typeof BenchmarkMetricSchema>;

/** Population statistics, available whether or not the store has a row. */
export const BenchmarkMedianSchema = z.object({
  all: z.number().nullable(),
  industry: z.number().nullable(),
  peer: z.number().nullable(),
  /**
   * How many stores with this metric the industry and peer medians rest on.
   * Optional because medians read before they were counted have none.
   */
  industryCount: z.number().nullable().optional(),
  peerCount: z.number().nullable().optional(),
});

export type BenchmarkMedian = z.infer<typeof BenchmarkMedianSchema>;

export const StoreBenchmarkRowSchema = z.object({
  shopifyDomain: z.string(),
  industry: z.string().nullable(),
  subIndustry: z.string().nullable(),
  totalSessions: z.number().nullable(),
  peerCount: z.number().nullable(),
  /**
   * Which instrument measured this store's own funnel.
   *
   * `pixel` is a row from the rollup: our own measurement, every stage, and
   * the peer group that goes with it.
   *
   * `shopifyCalibrated` is a store our pixel has never seen — every store
   * minutes after install, and the case this feature exists for. Its funnel
   * comes from Shopify's sessions dataset and is corrected onto our scale
   * before being placed, because the two instruments do not count the same
   * quantities: only conversion rate is comparable between them untreated.
   *
   * **It travels on the row because the row is otherwise indistinguishable.**
   * A derived funnel carries real percentiles against a real population, so
   * nothing about the numbers says how they were arrived at — and the one
   * thing a merchant must never be told is that we measured something we
   * inferred.
   *
   * Two things are true of `shopifyCalibrated` and not of `pixel`, and both
   * are visible in the data rather than needing to be remembered: `pdpRate` is
   * absent, because Shopify's sessions dataset has no product-page-view
   * metric, and `percentilePeer` is null, because a store with no row of its
   * own has no peer group to be placed in.
   */
  source: z.enum(["pixel", "shopifyCalibrated"]),
  metrics: z.record(BenchmarkMetricKeySchema, BenchmarkMetricSchema),
});

export type StoreBenchmarkRow = z.infer<typeof StoreBenchmarkRowSchema>;

export const StoreBenchmarksRequestBodySchema = z.object({
  period: BenchmarkPeriodSchema.default("L90D").describe(
    "Trailing window. L90D by default, matching the window the audit sizes tests over."
  ),
  industry: z
    .string()
    .optional()
    .describe(
      "Industry to take medians for when the store has no row of its own. Ignored when it does."
    ),
});

export type StoreBenchmarksRequestBodyType = z.infer<
  typeof StoreBenchmarksRequestBodySchema
>;

export const StoreBenchmarksResponseBodySchema = z.object({
  period: BenchmarkPeriodSchema,
  refreshDate: z.string().nullable(),
  allStoreCount: z.number().nullable(),
  /** Null when our pixel has no history for this store — the new-install case. */
  store: StoreBenchmarkRowSchema.nullable(),
  /**
   * Always populated when the population has data, regardless of `store`.
   * This is what makes a new store comparable: its own funnel, measured from
   * Shopify, against what normal looks like here.
   */
  medians: z.record(BenchmarkMetricKeySchema, BenchmarkMedianSchema),
  /**
   * Which rungs of `medians` are actually about this store.
   *
   * A median row carries three populations, and only some of them belong to
   * the store we asked about. Without this the three are indistinguishable,
   * so a reader either trusts all of them — quoting a stranger's peer group
   * as this store's — or trusts only `all`, which is what the audit does
   * today and why resolving an industry buys nothing yet.
   *
   * - `store`: the store's own row. Peer, industry and all are all its own.
   * - `industry`: a row from the industry that was asked for. Industry and
   *   all hold; `peer` is some other store's peer group.
   * - `all`: an arbitrary row, taken because no industry was asked for or
   *   none matched. Only `all` holds — every row carries the same all-store
   *   figures, and its peer and industry columns are that store's.
   */
  medianBasis: z.enum(["store", "industry", "all"]),
  /**
   * The industry the medians describe, or null when we cannot say.
   *
   * Null on the `all` basis. The row that supplied those medians has an
   * industry of its own and it is nobody's answer, so naming it would read as
   * "we compared you against apparel stores" when we did not.
   */
  medianIndustry: z.string().nullable(),
});

export type BenchmarkMedianBasis =
  StoreBenchmarksResponseBodyType["medianBasis"];

export type StoreBenchmarksResponseBodyType = z.infer<
  typeof StoreBenchmarksResponseBodySchema
>;
